/**
 * Renders a value of unknown type as display text, listing what is renderable rather than what is
 * not: objects and functions have no useful string form — `String({})` is `[object Object]` — so
 * they read as absent here rather than reaching the UI. Returns `null` so callers pick their own
 * placeholder.
 */
export const asDisplayText = (value: unknown): string | null => {
  switch (typeof value) {
    case 'string':
      return value
    case 'number':
    case 'bigint':
    case 'boolean':
    case 'symbol':
      return String(value)
    default:
      return null
  }
}

export const getStringInitials = (name: string | null | undefined): string => {
  if (!name) return ''
  return name
    .split(' ')
    .map(part => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

export const formatPhoneNumber = (phoneNumber: string | null | undefined): string => {
  const cleaned = (phoneNumber ?? '').replace(/\D/g, '')
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : (phoneNumber ?? '')
}

/** The ten national digits of a typed or pasted US number, dropping a leading country code 1. */
export const toPhoneDigits = (phoneNumber: string | null | undefined): string => {
  const digits = (phoneNumber ?? '').replace(/\D/g, '')
  const nationalDigits = digits.length > 10 && digits.startsWith('1') ? digits.slice(1) : digits
  return nationalDigits.slice(0, 10)
}

/** Formats up to ten phone digits as (xxx) xxx-xxxx while they are still being typed. */
export const formatPartialPhoneNumber = (digits: string): string => {
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

export const titleCase = (str: string) =>
  str
    .replace(/(^|[_-])([a-z])/g, (_match: string, _separator: string, letter: string) =>
      letter.toUpperCase(),
    )
    .replace(
      /([a-z])([A-Z])/g,
      (_match: string, lower: string, upper: string) => `${lower} ${upper}`,
    )
